import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchUserData,
  postVerifier,
  postClearMustChangeSecretKey,
  postChangeUserPassword,
  getUserWrappedDeks,
  postUserRewrapDeks,
  postApiKey,
  postUserInvite,
  getUserInvites,
  getOrganizationAvailableCredits,
  getUserLogs,
  getUserFromInvite,
  getUserInvite,
  postAnswerInvite,
  cancelSentInvite,
  editInvitedUser,
  removeUserFromOrganization,
  getInviteDetails,
  postBatchInvite,
  updateUseSnapLogo,
  getEula,
  acceptTerms,
  getOrganizationUsers,
  editOrganizationUser,
  removeOrganizationUser,
  updatePendingInvite,
  postRequestPasswordReset,
  postDismissPasswordResetRequest,
  getPasswordResetRequests,
} from "~/services/gateways/user.gateway";
import type {
  EncryptedData,
  UserData,
  UserInvitesFilters,
  UserLogsFilters,
  OrganizationUsersFilters,
  InviteUpdateRequest,
} from "~/types/global";
import type { BatchInvitePayload } from "~/services/gateways/user.gateway";
import { tanstackQueryConfig } from "~/helpers/constants";
import { usePermissionCheck } from "~/components/router/PermissionGuard";
import { Permission } from "~/helpers/permissions.helper";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { usePermissionManager } from "~/services/permissionManager";

export const useUserCRUD = () => {
  const queryClient = useQueryClient();
  const { checkPermission } = usePermissionCheck();
  const queryKey = ["user"];
  const { isAdmin } = usePermissionManager();

  const useQueryUser = useQuery<UserData>({
    queryKey: queryKey,
    queryFn: fetchUserData,
    staleTime: Infinity,
    retry: false,
  });

  const addNewVerifierMutation = useMutation({
    mutationFn: async (data: EncryptedData) => {
      try {
        await postVerifier(data);
        return true;
      } catch (error) {
        console.error("addNewVerifierMutation Error:", error);
        return false;
      }
    },
    onSuccess: (ok) => {
      if (ok) {
        invalidateUser();
        //toast.success("Senha criada com sucesso!")
      }
    },
    onError: createErrorHandler(
      "Erro ao tentar salvar senha",
      "Erro ao salvar"
    ),
  });

  const clearMustChangeSecretKeyMutation = useMutation({
    mutationFn: async (data: EncryptedData) => {
      await postClearMustChangeSecretKey(data);
      return true;
    },
    onSuccess: () => {
      invalidateUser();
    },
    onError: createErrorHandler(
      "Erro ao alterar senha",
      "Erro ao salvar nova senha"
    ),
  });

  const changeUserPasswordMutation = useMutation({
    mutationFn: async (data: EncryptedData) => {
      await postChangeUserPassword(data);
      return true;
    },
    // Não invalida o user — o usuário será deslogado logo após
    onError: createErrorHandler(
      "Erro ao alterar senha de criptografia",
      "Erro ao salvar nova senha"
    ),
  });

  const rewrapDeksMutation = useMutation({
    mutationFn: async (items: Array<{
      id: string;
      new_user_wrapped_dek: string;
      new_user_dek_iv: string;
      item_type: string;
    }>) => {
      if (items.length === 0) return;
      await postUserRewrapDeks(items);
    },
    onError: createErrorHandler(
      "Erro ao re-criptografar dados",
      "Erro ao alterar senha"
    ),
  });

  const invalidateUser = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKey,
      exact: true,
    });
  };

  const organizationAvailableCreditsQuery = (enabled = true) =>
    useQuery({
      queryKey: ["user", "organization", "availableCredits"],
      queryFn: getOrganizationAvailableCredits,
      ...tanstackQueryConfig,
      enabled: enabled && checkPermission(Permission.INVITE_USER),
    });

  const invitesQuery = (filters: UserInvitesFilters = {}, enabled = true) =>
    useQuery({
      queryKey: ["user", "invites", filters],
      queryFn: () => getUserInvites(filters),
      // Sobrescreve tanstackQueryConfig porque mudanças de status (aceito,
      // negado, cancelado) são feitas em outras sessões/navegadores e o admin
      // precisa enxergar o estado atualizado. Sem isto a lista trava no
      // primeiro fetch (antes havia `staleTime: Infinity` + `refetchOn*=false`,
      // então só atualizava após invalidate explícito — que nunca chega
      // cross-machine).
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      retry: false,
      enabled: enabled && checkPermission(Permission.GET_ORGANIZATION_INVITE),
    });

  const userLogsQuery = (filters: UserLogsFilters = {}, enabled = true) =>
    useQuery({
      queryKey: ["user", "logs", filters],
      queryFn: () => getUserLogs({ ...filters, is_user: isAdmin() ? filters.is_user : true }),
      ...tanstackQueryConfig,
      refetchOnMount: true,
      enabled:
        enabled && (checkPermission(Permission.GET_USER_LOGS) ||
          checkPermission(Permission.GET_ORGANIZATION_LOGS)),
    });

  const userInviteQuery = (status_invite?: string, type_invite?: string) =>
    useQuery({
      queryKey: ["user", "invite", status_invite, type_invite],
      queryFn: () => getUserInvite(status_invite, type_invite),
      ...tanstackQueryConfig,
      retry: false,
      enabled: checkPermission(Permission.GET_USER_INVITE),
    });

  // query com configurações diferentes para o refetch sempre que abrir o modal
  const userInviteDialogQuery = (status_invite?: string, type_invite?: string) =>
    useQuery({
      queryKey: ["user", "invite", status_invite, type_invite],
      queryFn: () => getUserInvite(status_invite, type_invite),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      enabled: checkPermission(Permission.GET_USER_INVITE),
    });

  const invalidadeUserInvite = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["user", "invite"],
      exact: false,
    });
  };

  const userFromInviteQuery = (invite_id: string) =>
    useQuery({
      queryKey: ["user", "fromInvite", invite_id],
      queryFn: () => getUserFromInvite(invite_id),
      ...tanstackQueryConfig,
      enabled:
        !!invite_id && checkPermission(Permission.GET_DATA_FROM_USER_INVITE),
      staleTime: Infinity,
      retry: false,
    });

  const inviteDetailsQuery = (invite_id: string) =>
    useQuery({
      queryKey: ["invite", "details", invite_id],
      queryFn: () => getInviteDetails(invite_id),
      ...tanstackQueryConfig,
      enabled: !!invite_id && checkPermission(Permission.GET_INVITE_DETAILS),
      staleTime: Infinity,
      retry: false,
    });

  const setApiKeyMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      if (!checkPermission(Permission.ADD_API_KEY)) {
        throw new Error("Você não tem permissão para configurar API Key");
      }
      return postApiKey(apiKey);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKey,
        exact: true,
      });
      toast.success("API Key configurada com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar configurar a chave da API",
      "Erro ao configurar chave da API"
    ),
  });

  const createUserInviteMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!checkPermission(Permission.INVITE_USER)) {
        throw new Error("Você não tem permissão para convidar usuários");
      }
      return postUserInvite(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
      toast.success("Convite enviado com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar enviar o convite",
      "Erro ao enviar convite"
    ),
  });

  const answerInviteMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!checkPermission(Permission.ANSWER_INVITE)) {
        throw new Error("Você não tem permissão para responder convites");
      }
      return postAnswerInvite(data);
    },
    onSuccess: () => {
      // Invalida tanto a visão do convidado (`user/invite`) quanto a do admin
      // (`user/invites`) — a segunda só importa se a mesma conta tem ambos os
      // papéis na sessão; cross-account o polling do invitesQuery cobre.
      queryClient.invalidateQueries({ queryKey: ["user", "invite"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["user", "invites"], exact: false });
    },
    onError: createErrorHandler(
      "Erro ao tentar responder o convite",
      "Erro ao responder convite"
    ),
  });

  const acceptInviteMutation = useMutation({
    mutationFn: async ({
      data,
      loadingToast,
    }: {
      data: any;
      loadingToast: string | number;
    }) => {
      if (!checkPermission(Permission.ANSWER_INVITE)) {
        throw new Error("Você não tem permissão para responder convites");
      }
      const result = await postAnswerInvite(data);
      return { result, loadingToast };
    },
    onSuccess: async ({ loadingToast }) => {
      toast.dismiss(loadingToast);
      // Aceitar convite muda org/role/permissões — backend já invalidou as
      // sessões Keycloak via force_logout_by_user_id e setou flag de
      // invalidação no Redis. O front PRECISA fazer reload completo (não
      // navigate do React Router) para zerar React Query cache, Zustand,
      // contexts, e forçar o browser a refetch /login. Sem isso, o SPA fica
      // com estado stale até a próxima chamada falhar com 401.
      toast.success("Convite aceito", {
        description:
          "Você será desconectado agora. Faça login novamente para que as novas permissões e organização entrem em vigor.",
        duration: 3000,
      });
      // Pequeno delay para o toast aparecer antes do reload.
      setTimeout(() => {
        window.location.replace("/login");
      }, 1200);
    },
    onError: (error, { loadingToast }) => {
      toast.dismiss(loadingToast);
      createErrorHandler(
        "Erro ao tentar responder o convite",
        "Erro ao responder convite"
      )(error);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async (invite_id: string) => {
      if (!checkPermission(Permission.CANCEL_INVITE)) {
        throw new Error("Você não tem permissão para cancelar convites");
      }
      return cancelSentInvite(invite_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
      toast.success("Convite cancelado com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar cancelar o convite",
      "Erro ao cancelar convite"
    ),
  });

  const editInvitedUserMutation = useMutation({
    mutationFn: async ({
      user_id,
      report_types,
      role,
      credits_monthly,
    }: {
      user_id: string;
      report_types: string[];
      role: string;
      credits_monthly: number;
    }) => {
      if (!checkPermission(Permission.EDIT_USER)) {
        throw new Error("Você não tem permissão para editar usuários");
      }
      return editInvitedUser(user_id, report_types, role, credits_monthly);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["user", "fromInvite"],
        exact: false,
      });
      toast.success("Usuário editado com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar editar o usuário",
      "Erro ao editar usuário"
    ),
  });

  const removeUserFromOrganizationMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!checkPermission(Permission.REMOVE_ORGANIZATION)) {
        throw new Error(
          "Você não tem permissão para remover usuários da organização"
        );
      }
      return removeUserFromOrganization(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
      toast.success("Usuário removido da organização com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar remover o usuário da organização",
      "Erro ao remover usuário da organização"
    ),
  });

  const updateUseSnapLogoMutation = useMutation({
    mutationFn: async (print_snap_logo: boolean) => {
      if (!checkPermission(Permission.PRINT_SNAP_LOGO)) {
        throw new Error(
          "Você não tem permissão para alterar a visualização do logo do SNAP"
        );
      }
      return updateUseSnapLogo(print_snap_logo);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user"],
        exact: true,
      });
      toast.success("Opção de usar logo do SNAP alterada com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar alterar a visualização do logo do SNAP",
      "Erro ao alterar visualização do logo do SNAP"
    ),
  });

  const eulaQuery = useQuery({
    queryKey: ["eula"],
    queryFn: getEula,
    ...tanstackQueryConfig,
    staleTime: Infinity,
    retry: false,
    enabled: useQueryUser.data ? !useQueryUser.data.accept_terms : false,
  });

  const acceptTermsMutation = useMutation({
    mutationFn: async () => {
      if (!checkPermission(Permission.ACCEPT_TERMS)) {
        throw new Error("Você não tem permissão para aceitar os termos de uso");
      }
      return acceptTerms();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user"],
        exact: true,
      });

      toast.success("Termos de uso aceitos com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar aceitar os termos de uso",
      "Erro ao aceitar termos de uso"
    ),
  });

  // ===== Organization Users (new separated tab) =====

  const organizationUsersQuery = (filters: OrganizationUsersFilters = {}, enabled = true) =>
    useQuery({
      queryKey: ["organization", "users", filters],
      queryFn: () => getOrganizationUsers(filters),
      ...tanstackQueryConfig,
      retry: false,
      enabled: enabled && checkPermission(Permission.GET_ORGANIZATION_USERS),
    });

  const editOrganizationUserMutation = useMutation({
    mutationFn: async ({
      user_id,
      report_types,
      role,
      credits_monthly,
      type_license,
    }: {
      user_id: string;
      report_types: string[];
      role: string;
      credits_monthly: number;
      type_license?: "recorrente" | "poc";
    }) => {
      if (!checkPermission(Permission.EDIT_ORGANIZATION_USER)) {
        throw new Error("Você não tem permissão para editar usuários");
      }
      return editOrganizationUser(user_id, report_types, role, credits_monthly, type_license);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization", "users"],
        exact: false,
      });
      toast.success("Usuário editado com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar editar o usuário",
      "Erro ao editar usuário"
    ),
  });

  const removeOrganizationUserMutation = useMutation({
    mutationFn: async (user_id: string) => {
      if (!checkPermission(Permission.REMOVE_ORGANIZATION)) {
        throw new Error("Você não tem permissão para remover usuários da organização");
      }
      return removeOrganizationUser(user_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization", "users"],
        exact: false,
        refetchType: "all",
      });
      toast.success("Usuário removido da organização com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar remover o usuário da organização",
      "Erro ao remover usuário da organização"
    ),
  });

  const updatePendingInviteMutation = useMutation({
    mutationFn: async ({ invite_id, data }: { invite_id: string; data: InviteUpdateRequest }) => {
      if (!checkPermission(Permission.EDIT_USER)) {
        throw new Error("Você não tem permissão para editar convites pendentes");
      }
      return updatePendingInvite(invite_id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
      toast.success("Convite atualizado com sucesso!");
    },
    onError: createErrorHandler(
      "Erro ao tentar atualizar o convite",
      "Erro ao atualizar convite"
    ),
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: postRequestPasswordReset,
    onSuccess: () => {
      invalidateUser();
      toast.success("Solicitação enviada. Um administrador irá redefinir sua senha em breve.");
    },
    onError: createErrorHandler(
      "Erro ao solicitar reset de senha",
      "Erro ao solicitar reset"
    ),
  });
  const dismissPasswordResetRequestMutation = useMutation({
    mutationFn: (userId: string) => postDismissPasswordResetRequest(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["password-reset-requests"] });
      queryClient.invalidateQueries({ queryKey: ["organization", "users"], exact: false });
    },
    onError: createErrorHandler(
      "Erro ao dispensar solicitação",
      "Erro"
    ),
  });

  const batchInviteMutation = useMutation({
    mutationFn: async (data: BatchInvitePayload) => {
      if (!checkPermission(Permission.INVITE_USER)) {
        throw new Error("Você não tem permissão para convidar usuários");
      }
      return postBatchInvite(data);
    },
    onSuccess: (response) => {
      const successes = response.results.filter(r => r.status === "sent");
      const failures = response.results.filter(r => r.status === "error");

      if (successes.length > 0) {
        toast.success(`${successes.length} convite(s) enviado(s) com sucesso!`);
      }

      failures.forEach(f => {
        toast.error(`Falha ao convidar ${f.email}: ${f.reason}`);
      });

      queryClient.invalidateQueries({
        queryKey: ["user", "invites"],
        exact: false,
      });
    },
    onError: createErrorHandler(
      "Erro ao enviar convites em lote",
      "Erro ao enviar convites"
    ),
  });

  const passwordResetRequestsQuery = (enabled = true) =>
    useQuery({
      queryKey: ["password-reset-requests"],
      queryFn: getPasswordResetRequests,
      ...tanstackQueryConfig,
      refetchOnMount: true,
      retry: false,
      enabled: enabled && isAdmin() && checkPermission(Permission.VIEW_PASSWORD_RESET_REQUESTS),
    });

  return {
    useQueryUser,
    addNewVerifierMutation,
    clearMustChangeSecretKeyMutation,
    changeUserPasswordMutation,
    rewrapDeksMutation,
    organizationAvailableCreditsQuery,
    invitesQuery,
    userLogsQuery,
    userInviteQuery,
    userInviteDialogQuery,
    userFromInviteQuery,
    inviteDetailsQuery,
    setApiKeyMutation,
    createUserInviteMutation,
    answerInviteMutation,
    acceptInviteMutation,
    cancelInviteMutation,
    editInvitedUserMutation,
    removeUserFromOrganizationMutation,
    invalidateUser,
    invalidadeUserInvite,
    updateUseSnapLogoMutation,
    eulaQuery,
    acceptTermsMutation,
    organizationUsersQuery,
    editOrganizationUserMutation,
    removeOrganizationUserMutation,
    updatePendingInviteMutation,
    batchInviteMutation,
    requestPasswordResetMutation,
    dismissPasswordResetRequestMutation,
    passwordResetRequestsQuery,
  };
};
